// @ts-nocheck
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../const/CommonConst';

/**
 * Custom React hook for managing and synchronizing a state variable with URL query parameters.
 *
 * @param {*} initialState - The initial state value.
 * @param {string} paramsName - The name of the query parameter to associate with the state.
 * @param {function} [customSerialize] - (Optional) Function to convert the state value into a string representation for the URL.
 *   Default behavior is to call `toString()` on the state value.
 * @param {function} [customDeserialize] - (Optional) Function to convert a string representation from the URL into the state value.
 *   Default behavior is to use the identity function, which returns the input string as is.
 * @returns {Array} - An array containing the current state value as the first element and a function to update it as the second element.
 *
 * @typedef {function} SerializeFunction
 * @param {*} state - The state value to be converted into a string representation.
 * @returns {string} - The string representation of the state value.
 *
 * @typedef {function} DeserializeFunction
 * @param {string} serializedState - The string representation of the state value from the URL.
 * @returns {*} - The state value parsed from the string representation.
 *
 * @example
 * // Usage with a boolean state variable
 * const [bool, setBool] = useStateParams(false, 'boolean',(s) => (s ? 'true' : 'false'),(s) => s === 'true');
 *
 * // Usage with a numeric state variable
 * const [slider, setSlider] = useStateParams(10, 'slider',(s) => s.toString(),(s) => (Number(s) !== Number.NaN ? Number(s) : 10));
 */
export default function useStateParams(
  initialState,
  paramsName,
  customSerialize = (s) => s.toString(),
  customDeserialize = (s) => s
) {
  let serialize = (s) => s.toString();
  let deserialize = (s) => s;

  switch (typeof initialState) {
    case 'boolean':
      serialize = (s) => (s ? 'true' : 'false');
      deserialize = (s) => s === 'true';
      break;
    case 'number':
      serialize = (s) => s.toString();
      deserialize = (s) =>
        !Number.isNaN(Number(s)) ? Number(s) : initialState;
      break;
    case 'object':
      serialize = (s) => JSON.stringify(s);
      deserialize = (s) => JSON.parse(decodeURIComponent(s));
      break;
    default:
      serialize = customSerialize;
      deserialize = customDeserialize;
  }

  const navigate = useNavigate();
  const { search } = useLocation();

  const searchParams = new URLSearchParams(search);
  const existingValue = searchParams.get(paramsName);
  const [state, setState] = useState(
    existingValue ? deserialize(existingValue) : initialState
  );

  useEffect(() => {
    // Updates state when the user navigates backwards or forwards in browser history
    if (existingValue && deserialize(existingValue) !== state) {
      setState(deserialize(existingValue));
    }
  }, [existingValue]);

  /**
   * Updates the state and synchronizes it with the URL query parameters.
   * Provides options to either clear all other query parameters or preserve them while updating the specific parameter.
   *
   * @param {*} s - The new state value or a function that receives the previous state and returns a new state.
   *   - If `s` is a function, it will be invoked with the current state as an argument to derive the new state.
   * @param {boolean} [clearParams=false] - Determines whether to clear all existing query parameters and only set the current parameter.
   *   - `true`: Clears all existing query parameters and sets only the specified one.
   *   - `false`: Preserves existing query parameters and updates/sets the specified one.
   * @param {Object} [prevStateData=[]] - Optional state data to persist across navigation.
   *   - Used to pass additional state information (`state` property of React Router's `navigate`) when updating the query parameter.
   *
   *  @param {Object} [additionalParams={}] - Additional query parameters to set in the URL.
   *   - Object where keys are parameter names and values are parameter values.
   *   - These parameters will be added alongside the main parameter being updated.
   *
   *
   * @returns {void}
   *
   * @example
   * // Update a specific query parameter while preserving others
   * onChange('newValue');
   *
   * @example
   * // Clear all query parameters and set only one
   * onChange('newValue', true);
   *
   * @example
   * // Update state using a function and pass previous state data
   * onChange((prev) => prev + 1, false, { breadcrumb: 'Home > Page' });
   *
   * @description
   * - If `clearParams` is `true`, all existing query parameters are cleared, and only the specified parameter (`paramsName`) is set in the URL.
   * - If `clearParams` is `false`, the query parameter specified by `paramsName` is updated while preserving other parameters.
   * - `prevStateData` can be used to maintain `state` across route navigation, useful for persisting transient state.
   */
  const onChange = (
    s,
    clearParams = false,
    prevStateData = EMPTY_ARRAY,
    replace = false,
    additionalParams = EMPTY_OBJECT
  ) => {
    setState(s);
    const param = typeof s === 'function' ? s(state) : s;
    if (clearParams) {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set(paramsName, serialize(param));

      // Add additional parameters to the fresh URLSearchParams
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          newSearchParams.set(key, value.toString());
        }
      });

      navigate(
        { search: `?${newSearchParams.toString()}` },
        { state: prevStateData, replace }
      );
    } else {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(paramsName, serialize(param));

      // Add additional parameters to existing URLSearchParams
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          newSearchParams.set(key, value.toString());
        }
      });
      navigate(
        { search: `?${newSearchParams.toString()}` },
        { state: prevStateData, replace }
      );
    }
  };

  return [state, onChange];
}
